<template>
  <q-layout>
    <q-page-container>
      <div class="row q-pa-md">
        <div class="row col-12 bg-white q-mt-md chart-box q-pa-md filter-box">
          <div class="text-primary q-mb-sm text-h6">Filter</div>
          <date-picker v-model="input.dateRange" range value-type="date" placeholder="Select date range" class="col-12"
            :shortcuts="dateShortcuts"></date-picker>
          <div class="col-12 row justify-end q-gutter-x-sm q-mt-sm">
            <q-btn :disable="!exportable" class="q-mt-md" label="Export" no-caps no-wrap color="accent"
              @click="onExport"></q-btn>
            <q-btn :loading="loading" class="q-mt-md" label="Apply" no-caps no-wrap color="primary"
              @click="onApply"></q-btn>
          </div>
        </div>

        <!-- Customer Ranking Table -->
        <div class="col-12 bg-white q-mt-md chart-box q-pa-md" v-if="rawData && rawData.length > 0">
          <div class="row items-center justify-between q-mb-md">
            <div class="text-primary text-h6">Customer Ranking</div>
            <div class="row items-center q-gutter-sm">
              <span v-if="selectedCustomers.length > 0" class="text-caption text-grey">
                {{ selectedCustomers.length }} selected
              </span>
              <q-btn :disable="selectedCustomers.length < 2" color="accent" label="Compare Selected" icon="show_chart"
                no-caps @click="showComparisonChart" />
            </div>
          </div>
          <q-table flat bordered :data="rankingTableData" :columns="rankingColumns" row-key="id"
            :pagination="{ rowsPerPage: 20 }" selection="multiple" :selected.sync="selectedCustomers"
            class="ranking-table">
            <template v-slot:body-cell-action="props">
              <q-td :props="props">
                <q-btn flat dense color="primary" icon="visibility" size="sm"
                  @click.stop="goToCustomerDetail(props.row)">
                  <q-tooltip>View Detail</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </div>

        <!-- Time Series Comparison Chart -->
        <div ref="comparisonChart" class="col-12 bg-white q-mt-md chart-box q-pa-md"
          v-if="showTimeSeries && comparisonSeries.length > 0">
          <div class="row items-center justify-between q-mb-md">
            <div class="text-primary text-h6">Sales Comparison Over Time</div>
            <q-btn flat dense icon="close" color="grey" @click="showTimeSeries = false" />
          </div>
          <div class="q-mb-sm">
            <q-chip v-for="customer in selectedCustomersForChart" :key="customer.id"
              :style="{ backgroundColor: customer.color, color: 'white' }" dense>
              {{ customer.name }}
            </q-chip>
          </div>
          <apexchart type="line" height="400" :options="comparisonChartOptions" :series="comparisonSeries" />
        </div>

        <div class="chart-content row col-12" v-if="rawData">
          <div class="col-12 bg-white q-mt-md chart-box q-pa-md">
            <div class="row full-width justify-between">
              <div class="text-primary text-h6">Sales by Customer</div>
              <q-toggle v-model="isSort.sale" label="Sort by value" left-label />
            </div>
            <horizontal-bar-chart ref="sale_chart" id="sale-chart" :chart-data="saleDatacollection"
              :options="saleOptions" :styles="chartStyle" @click="onChartClick"></horizontal-bar-chart>
          </div>

          <!-- <div class="col-12 bg-white q-mt-md chart-box q-pa-md">
            <div class="row full-width justify-between">
              <div class="text-primary text-h6">Orders by Customer</div>
              <q-toggle v-model="isSort.order" label="Sort by value" left-label />
            </div>
            <horizontal-bar-chart id="order-chart" :chart-data="orderDatacollection" :options="orderOptions"
              :styles="chartStyle"></horizontal-bar-chart>
          </div> -->
        </div>

        <!-- No data message -->
        <div class="col-12 bg-white q-mt-md chart-box q-pa-md text-center" v-if="rawData && rawData.length === 0">
          <q-icon name="info" size="48px" color="grey" />
          <div class="text-grey q-mt-md">No data for selected period</div>
        </div>
      </div>
    </q-page-container>
  </q-layout>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { QCard, QCardSection, QCardActions, QSelect } from "quasar";
import HorizontalBarChart from "./HorizontalBarChart.js";
import dayjs from "dayjs";
import { mapState } from "vuex";

const components = { QCard, QCardSection, QCardActions, QSelect };

export default {
  name: "SalesByCustomerStat",
  components: {
    ...components,
    HorizontalBarChart,
    DatePicker,
  },
  data() {
    return {
      lang: {
        formatLocale: {
          firstWeekContainsDate: 4,
        },
      },
      loading: false,
      isSort: {
        sale: true,
        order: true,
      },
      rawData: undefined,
      saleOptions: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            title: function (tooltipItem, data) {
              return data["labels"][tooltipItem[0]["index"]];
            },
            label: function (tooltipItem, data) {
              return new Intl.NumberFormat().format(
                data["datasets"][0]["data"][tooltipItem["index"]]
              );
            },
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Day",
            },
          },
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Customer name",
              },
              ticks: {
                beginAtZero: true,
                min: 0,
              },
            },
          ],
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Revenue (฿)",
              },
              ticks: {
                beginAtZero: true,
                callback: (value, index, values) => {
                  return new Intl.NumberFormat().format(value);
                },
              },
            },
          ],
        },
      },
      orderOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: true,
              text: "Day",
            },
          },
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Customer name",
              },
              ticks: {
                beginAtZero: true,
              },
            },
          ],
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Number of orders",
              },
              ticks: {
                beginAtZero: true,
                precision: 0,
                callback: (value, index, values) => {
                  return new Intl.NumberFormat().format(value);
                },
              },
            },
          ],
        },
      },
      input: {
        dateRange: null,
      },
      selectedCustomers: [],
      showTimeSeries: false,
      dailyDataByCustomer: {}, // { customerId: [{ date, sales }] }
      selectedCustomersForChart: [],
      comparisonSeries: [],
      chartColors: [
        "#2c2a74", "#e91e63", "#4caf50", "#ff9800", "#2196f3",
        "#9c27b0", "#00bcd4", "#ff5722", "#795548", "#607d8b",
      ],
      comparisonChartOptions: {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          type: "datetime",
          labels: {
            datetimeFormatter: {
              year: "yyyy",
              month: "MMM 'yy",
              day: "dd MMM",
            },
          },
        },
        yaxis: {
          title: { text: "Sales (฿)" },
          labels: {
            formatter: (value) => new Intl.NumberFormat("th-TH").format(value),
          },
        },
        tooltip: {
          x: { format: "dd MMM yyyy" },
          y: {
            formatter: (value) => new Intl.NumberFormat("th-TH").format(value) + " ฿",
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        markers: {
          size: 4,
          hover: { size: 6 },
        },
        grid: {
          borderColor: "#e7e7e7",
        },
      },
      dateShortcuts: [
        {
          text: "Today",
          onClick() {
            const today = new Date();
            return [today, today];
          },
        },
        {
          text: "Yesterday",
          onClick() {
            const yesterday = dayjs().subtract(1, "day").toDate();
            return [yesterday, yesterday];
          },
        },
        {
          text: "Last 7 Days",
          onClick() {
            const end = new Date();
            const start = dayjs().subtract(6, "day").toDate();
            return [start, end];
          },
        },
        {
          text: "Last 30 Days",
          onClick() {
            const end = new Date();
            const start = dayjs().subtract(29, "day").toDate();
            return [start, end];
          },
        },
        {
          text: "This Month",
          onClick() {
            const start = dayjs().startOf("month").toDate();
            const end = dayjs().endOf("month").toDate();
            return [start, end];
          },
        },
        {
          text: "Last Month",
          onClick() {
            const start = dayjs().subtract(1, "month").startOf("month").toDate();
            const end = dayjs().subtract(1, "month").endOf("month").toDate();
            return [start, end];
          },
        },
      ],
      height: 150,
      rankingColumns: [
        {
          name: "rank",
          label: "#",
          align: "center",
          field: "rank",
          style: "width: 60px",
        },
        {
          name: "name",
          label: "Customer",
          align: "left",
          field: "name",
          sortable: true,
        },
        {
          name: "phone",
          label: "Phone",
          align: "left",
          field: "phone",
        },
        {
          name: "shippingName",
          label: "Shipping Name",
          align: "left",
          field: "shippingName",
          sortable: true,
        },
        {
          name: "shippingAddress",
          label: "Shipping Address",
          align: "left",
          field: "shippingAddress",
          sortable: true,
          style: "max-width: 250px; white-space: normal;",
        },
        {
          name: "sales",
          label: "Sales (฿)",
          align: "right",
          field: "sales",
          format: (val) => new Intl.NumberFormat("th-TH").format(val),
          sortable: true,
        },
        {
          name: "orders",
          label: "Orders",
          align: "center",
          field: "orders",
          sortable: true,
        },
        {
          name: "action",
          label: "",
          align: "center",
          field: "id",
          style: "width: 80px",
        },
      ],
    };
  },
  computed: {
    ...mapState("admin", ["user", "groups"]),
    chartStyle() {
      return {
        height: `${this.height + this.rawData.length * 20}px`,
        position: "relative",
      };
    },
    isShow() {
      return this.rawData.length > 0 ? true : false;
    },
    sortedCustomerList() {
      if (!this.rawData) return [];
      const sorted = [...this.rawData];
      sorted.sort((a, b) => b.totalSaleVolume - a.totalSaleVolume);
      return sorted;
    },
    rankingTableData() {
      return this.sortedCustomerList.map((item, index) => {
        // Get shipping name and address from first address
        let shippingName = "";
        let shippingAddress = "";
        const customer = item.customer;
        if (customer?.address?.items?.length > 0) {
          const addr = customer.address.items[0];
          if (addr) {
            shippingName = `${addr.firstname || ""} ${addr.lastname || ""}`.trim();
            const addressParts = [
              addr.address,
              addr.subDistrict,
              addr.district,
              addr.province,
              addr.postcode,
            ].filter(Boolean);
            shippingAddress = addressParts.join(" ");
          }
        }

        return {
          id: item.id,
          rank: index + 1,
          name: `${customer?.firstname || ""} ${customer?.lastname || ""}`.trim(),
          phone: customer?.phone_number || "",
          shippingName: shippingName,
          shippingAddress: shippingAddress,
          sales: item.totalSaleVolume || 0,
          orders: item.totalOrder || 0,
        };
      });
    },
    saleDatacollection() {
      let labels = [];
      let data = [];

      this.rawData.forEach((d) => {
        if (d.customer) {
          if (d.customer.firstname || d.customer.lastname) {
            const label = `${d.customer.firstname} ${d.customer.lastname}`.trim();
            labels.push(label);
          } else {
            labels.push(d.customer.phone_number || d.id);
          }
        } else {
          labels.push(d.id);
        }
        data.push(d.totalSaleVolume);
      });

      // sort
      if (this.isSort.sale) {
        const toSorted = labels.map((l, index) => {
          return {
            name: labels[index],
            value: data[index],
          };
        });

        toSorted.sort((a, b) => {
          if (a.value > b.value) return -1;
          if (a.value < b.value) return 1;
          return 0;
        });

        labels = toSorted.map((s) => s.name);
        data = toSorted.map((s) => s.value);
      }
      // end sort

      return {
        labels: labels,
        color: [],
        datasets: [
          {
            label: "Sales",
            data: data,
            backgroundColor: "#2c2a74",
          },
        ],
      };
    },
    orderDatacollection() {
      let labels = [];
      let data = [];

      this.rawData.forEach((d) => {
        if (d.customer) {
          if (d.customer.firstname || d.customer.lastname) {
            const label = `${d.customer.firstname} ${d.customer.lastname}`.trim();
            labels.push(label);
          } else {
            labels.push(d.customer.phone_number || d.id);
          }
        } else {
          labels.push(d.id);
        }
        data.push(d.totalOrder);
      });

      // sort
      if (this.isSort.order) {
        const toSorted = labels.map((l, index) => {
          return {
            name: labels[index],
            value: data[index],
          };
        });

        toSorted.sort((a, b) => {
          if (a.value > b.value) return -1;
          if (a.value < b.value) return 1;
          return 0;
        });

        labels = toSorted.map((s) => s.name);
        data = toSorted.map((s) => s.value);
      }
      // end sort

      return {
        labels: labels,
        color: [],
        datasets: [
          {
            label: "Order",
            data: data,
            backgroundColor: "#2c2a74",
          },
        ],
      };
    },
    exportable() {
      if (this.rawData && this.rawData?.length > 0) {
        return true;
      }
      return false;
    },
  },
  watch: {},
  created() {
    // Default to current month
    const start = dayjs().startOf("month").toDate();
    const end = dayjs().toDate();
    this.input.dateRange = [start, end];
  },
  methods: {
    async onApply() {
      if (!this.input.dateRange || this.input.dateRange.length !== 2) {
        this.$q.notify({
          type: "warning",
          message: "Please select a date range",
        });
        return;
      }

      this.loading = true;
      this.selectedCustomers = [];
      this.showTimeSeries = false;
      this.dailyDataByCustomer = {};

      try {
        const [startDate, endDate] = this.input.dateRange;
        const start = dayjs(startDate).startOf("day");
        const end = dayjs(endDate).startOf("day");
        const diffDays = end.diff(start, "day") + 1;

        // Fetch data for each day and aggregate
        const allData = [];
        let current = start;

        for (let i = 0; i < diffDays; i++) {
          const dateKey = current.format("YYYY-MM-DD");
          try {
            const result = await this.$services.customerStat.listDailyStat(
              current.toDate()
            );
            if (result && result.length > 0) {
              allData.push(...result);
              // Store daily data by customer for time series
              result.forEach((stat) => {
                if (!stat.id) return;
                if (!this.dailyDataByCustomer[stat.id]) {
                  this.dailyDataByCustomer[stat.id] = [];
                }
                this.dailyDataByCustomer[stat.id].push({
                  date: current.valueOf(),
                  sales: stat.totalSaleVolume || 0,
                });
              });
            }
          } catch (e) {
            console.log(`No data for ${dateKey}`);
          }
          current = current.add(1, "day");
        }

        // Aggregate by customer ID
        this.rawData = this.aggregateByCustomer(allData);
        console.log("Aggregated data:", this.rawData);
        console.log("Daily data by customer:", this.dailyDataByCustomer);
      } catch (error) {
        console.error(error);
        this.$q.notify({
          type: "negative",
          message: "Failed to load data",
        });
      } finally {
        this.loading = false;
      }
    },
    aggregateByCustomer(list) {
      const customerMap = new Map();

      for (const stat of list) {
        if (!stat.id) continue;

        if (customerMap.has(stat.id)) {
          const existing = customerMap.get(stat.id);
          existing.totalSaleVolume = (existing.totalSaleVolume || 0) + (stat.totalSaleVolume || 0);
          existing.totalOrder = (existing.totalOrder || 0) + (stat.totalOrder || 0);
        } else {
          customerMap.set(stat.id, {
            ...stat,
            totalSaleVolume: stat.totalSaleVolume || 0,
            totalOrder: stat.totalOrder || 0,
          });
        }
      }

      return Array.from(customerMap.values());
    },
    goToCustomerDetail(row) {
      this.$router.push({
        name: "CustomerDetail",
        params: { id: row.id },
      });
    },
    onChartClick(event, elements) {
      if (elements && elements.length > 0) {
        const index = elements[0]._index;
        const customer = this.sortedCustomerList[index];
        if (customer) {
          this.goToCustomerDetail(customer);
        }
      }
    },
    onExport() {
      if (!(this.rawData && this.rawData?.length > 0)) {
        console.log("No data!");
        return;
      }
      try {
        const headers = {
          id: "Customer ID",
          name: "Name",
          shippingName: "Shipping Name",
          shippingAddress: "Shipping Address",
          phone: "Phone",
          sales: "Sales",
          order: "Order",
        };

        const dtStart = dayjs(this.input.dateRange[0]).format("YYYY-MM-DD");
        const dtEnd = dayjs(this.input.dateRange[1]).format("YYYY-MM-DD");
        let fileTitle = `Customer-report-${dtStart}_to_${dtEnd}`;

        if (dtStart === dtEnd) {
          fileTitle = `Customer-report-${dtStart}`;
        }

        console.log("dt:", dtStart, dtEnd, fileTitle);

        let itemsFormatted = [];

        this.rawData.forEach((data) => {
          let name = "";
          let phone = "";
          let shippingName = "";
          let shippingAddress = "";
          if (data.customer) {
            if (data.customer.firstname) {
              name = `${data.customer.firstname} `;
              if (data.customer.lastname) {
                name = name.concat(data.customer.lastname);
              }
            }
            phone = data.customer.phone_number || "";
            // Get shipping name and address from first address
            if (data.customer.address?.items?.length > 0) {
              const addr = data.customer.address.items[0];
              if (addr) {
                shippingName = `${addr.firstname || ""} ${addr.lastname || ""}`.trim();
                const addressParts = [
                  addr.address,
                  addr.subDistrict,
                  addr.district,
                  addr.province,
                  addr.postcode,
                ].filter(Boolean);
                shippingAddress = addressParts.join(" ");
              }
            }
          }

          itemsFormatted.push({
            id: data.id,
            name: this.escapeCSV(name.trim()),
            shippingName: this.escapeCSV(shippingName),
            shippingAddress: this.escapeCSV(shippingAddress),
            phone: phone ? `="${phone}"` : "",
            sales: data.totalSaleVolume,
            order: data.totalOrder,
          });
        });

        this.exportCSVFile(headers, itemsFormatted, fileTitle);
      } catch (error) {
        console.log(error);
      }
    },
    exportCSVFile(headers, items, fileTitle) {
      if (headers) {
        items.unshift(headers);
      }
      var jsonObject = JSON.stringify(items);
      var csv = this.convertToCSV(jsonObject);
      var exportedFilenmae = fileTitle + ".csv" || "export.csv";
      var blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, exportedFilenmae);
      } else {
        var link = document.createElement("a");
        if (link.download !== undefined) {
          var url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", exportedFilenmae);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },
    convertToCSV(objArray) {
      var array = typeof objArray != "object" ? JSON.parse(objArray) : objArray;
      var str = "";
      for (var i = 0; i < array.length; i++) {
        var line = "";
        for (var index in array[i]) {
          if (line != "") line += ",";
          line += array[i][index];
        }
        str += line + "\r\n";
      }
      return str;
    },
    escapeCSV(value) {
      if (value == null) return "";
      const str = String(value);
      // If contains comma, newline, or double quote, wrap in quotes and escape inner quotes
      if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    },
    showComparisonChart() {
      if (this.selectedCustomers.length < 2) {
        this.$q.notify({
          type: "warning",
          message: "Please select at least 2 customers to compare",
        });
        return;
      }

      // Get date range for x-axis
      const [startDate, endDate] = this.input.dateRange;
      const start = dayjs(startDate).startOf("day");
      const end = dayjs(endDate).startOf("day");

      // Build series for each selected customer
      const series = [];
      this.selectedCustomersForChart = [];

      this.selectedCustomers.forEach((customer, index) => {
        const color = this.chartColors[index % this.chartColors.length];
        const customerName = customer.name || customer.id;
        const dailyData = this.dailyDataByCustomer[customer.id] || [];

        // Create data points for the date range, filling in zeros for missing dates
        const dataPoints = [];
        let current = start;

        while (current.isBefore(end) || current.isSame(end, "day")) {
          const dateMs = current.valueOf();
          const found = dailyData.find((d) => {
            const dDate = dayjs(d.date);
            return dDate.isSame(current, "day");
          });
          dataPoints.push({
            x: dateMs,
            y: found ? found.sales : 0,
          });
          current = current.add(1, "day");
        }

        series.push({
          name: customerName,
          data: dataPoints,
        });

        this.selectedCustomersForChart.push({
          id: customer.id,
          name: customerName,
          color: color,
        });
      });

      // Update chart colors
      this.comparisonChartOptions = {
        ...this.comparisonChartOptions,
        colors: this.selectedCustomers.map(
          (_, i) => this.chartColors[i % this.chartColors.length]
        ),
      };

      this.comparisonSeries = series;
      this.showTimeSeries = true;

      // Scroll to chart
      this.$nextTick(() => {
        if (this.$refs.comparisonChart) {
          this.$refs.comparisonChart.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
  },
};
</script>

<style scoped>

.chart-box {
  border-radius: 10px;
}

>>>.mx-input {
  height: 40px !important;
}

>>>.chartjs-render-monitor {
  height: 600px;
}

.rank-badge-small {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2c2a74;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 13px;
  margin: 0 auto;
}

.customer-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.customer-contact {
  font-size: 13px;
  color: #666;
}

.sale-amount {
  font-size: 16px;
  font-weight: 600;
  color: #2c2a74;
}

.order-count {
  font-size: 12px;
  color: #888;
}

.ranking-table>>>tbody tr {
  cursor: pointer;
}

.ranking-table>>>tbody tr:hover {
  background: #f5f5ff !important;
}

.ranking-table>>>thead th {
  font-weight: 600;
  color: #2c2a74;
}

</style>
